import React, { useEffect, useState } from 'react'
import styled from 'styled-components'

import { useI18n } from '../hooks/use-i18n'
import { useInput } from '../hooks/use-input'
import { ICellResult, IPlayer } from '../types/common'

interface IProps {
  players: IPlayer[]
  selectedPlayers: Array<{ firstName: string; lastName: string; cells: ICellResult[] }>
  selectPlayer: (player: IPlayer) => void
}

export default function PlayerTextInput({ players, selectPlayer, selectedPlayers }: IProps) {
  const { t } = useI18n()
  const query = useInput('')
  const [proposedPlayers, setProposedPlayers] = useState<IPlayer[]>([])

  useEffect(() => {
    if (query.value.length === 0) {
      setProposedPlayers([])

      return
    }

    const playersCopy = [...players]
    const filteredPlayers = playersCopy.filter(
      (player) =>
        formatString(player.firstName).startsWith(formatString(query.value)) ||
        formatString(player.lastName).startsWith(formatString(query.value))
    )
    setProposedPlayers(
      filteredPlayers.filter((player) => {
        const inSelectedPlayer = selectedPlayers.some(
          (selectedPlayer) =>
            selectedPlayer.firstName === player.firstName && selectedPlayer.lastName === player.lastName
        )

        return !inSelectedPlayer
      })
    )
  }, [query.value, players, selectedPlayers])

  function formatString(str: string) {
    return str
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
  }

  function onPropositionClick(player: IPlayer) {
    selectPlayer(player)
    query.setValue('')
  }

  return (
    <Container>
      <input
        onChange={query.bind.onChange}
        placeholder={t('player-text-input.placeholder')}
        type="text"
        value={query.value}
      />
      <PropositionsContainer displayed={query.value.length > 0}>
        {proposedPlayers.map((player) => (
          <button
            className="proposed-player"
            key={`${player.firstName}-${player.lastName}`}
            onClick={() => onPropositionClick(player)}
          >
            <p>{`${player.firstName} ${player.lastName}`}</p>
          </button>
        ))}
      </PropositionsContainer>
    </Container>
  )
}

const Container = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  position: relative;
  width: 360px;
  z-index: 1;

  & > input {
    color: ${({ theme }) => theme.palette.text};
    font-size: 20px;
    border: ${({ theme }) => `solid 1px ${theme.palette.text}`};
    height: 64px;
    padding: 0 16px;
    width: 100%;
  }
`

const PropositionsContainer = styled.div<{ displayed: boolean }>`
  align-items: center;
  background-color: ${({ theme }) => theme.palette.background};
  display: flex;
  flex-direction: column;
  max-height: 480px;
  justify-content: flex-start;
  overflow: auto;
  left: 0;
  position: absolute;
  top: 64px;
  width: 100%;

  .proposed-player {
    align-items: center;
    border: ${({ theme }) => `solid 1px ${theme.palette.text}`};
    display: flex;
    flex-direction: row;
    flex-shrink: 0;
    height: ${({ displayed }) => (displayed ? '48px' : '0')};
    justify-content: flex-start;
    padding: 0 16px;
    transition: height 200ms ease-in-out;
    width: 100%;

    & > p {
      font-size: 16px;
    }
  }
`
